/**
 * <TutorSessionEntry> — une ligne de la session d'un élève avec le champion, vue du masteur (organism).
 *
 * La grammaire de la colonne de droite du live (Wajih, 02/11), une forme par sorte de ligne :
 *  - le texte du champion, **sans carte ni signature** (Wajih, 04/11 : sa tête est une fois en haut de la colonne,
 *    ses lignes n'ont plus à la répéter) : un paragraphe, coupé à quelques lignes avec « Lire la suite » ;
 *  - une **trace**, une ligne datée et grise : ce qui s'est passé ;
 *  - la **bulle** du masteur, à droite, à l'aplat de son univers ; une demande de génération dit ses
 *    réglages dessous ;
 *  - une **carte**, réservée à ce qui attend une décision : ici le résultat d'une génération, chaque
 *    proposition en version courte (`<GeneratedActivityCard compact>`, weekly du 03/11), « Voir en grand »
 *    qui les relit côte à côte dans une boîte de lecture, et « Régénérer » ;
 *  - le champion qui travaille (`<WaitingText>`), et le repère « Depuis ton dernier passage » ;
 *  - la génération : la ligne vivante, la carte « prêtes » qui ouvre la relecture au centre, « <champion>
 *    propose », et une activité de l'élève avec ses traces dessous (`TutorSessionGeneration.tsx`).
 *
 * Composes `<MessageBubble>`, `<GeneratedActivityResults>`, `<Divider>`, `<WaitingText>`,
 * `<TextButton>`, `<Text>`. Le champion arrive en prop (`helper`), jamais écrit ici : il signe « <champion> propose ».
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorSessionEntry } from "../../../ports/primary/ui/types/tutorLive"
import { type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { Banner } from "../../atoms/Banner"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { GeneratedActivityResults } from "../GeneratedActivityResults"
import { MessageBubble } from "../../molecules/MessageBubble"
import { Modal } from "../../molecules/Modal"
import { ActivityGroup, GenerationProgress, GenerationReady, HelperProposal } from "./TutorSessionGeneration"

/** Ce que le masteur fait d'une activité générée. */
export type TutorSessionResultAction = "create" | "show" | "quiz" | "regenerate" | "retry"

/**
 * Les gestes des lignes de la génération : « Revoir » et l'envoi de la première proposition (la carte « prêtes »),
 * « Voir », « Envoyer » et « Écarter » (« <champion> propose »).
 */
export type TutorSessionGenerationAction = "review" | "sendFirst" | "see" | "send" | "dismiss"

export type TutorSessionEntryProps = {
    entry: UITutorSessionEntry
    /** Le champion qui aide en séance : sa tête et son prénom signent ses lignes. */
    helper: ChampionName
    /**
     * Une activité générée, ajoutée, montrée, son quiz de prérequis basculé, ou régénérée (`activityId` :
     * laquelle des trois).
     */
    onResult?: (entryId: string, action: TutorSessionResultAction, activityId?: string | number) => void
    /** Un geste d'une ligne de la génération (la carte « prêtes », « <champion> propose »). */
    onGeneration?: (entryId: string, action: TutorSessionGenerationAction) => void
    /** Une proposition survolée dans la carte « prêtes » (web) : son aperçu au centre ; `null` quand le survol sort. */
    onPeek?: (proposalId: string | null) => void
    /** La proposition dont l'aperçu est au centre. */
    peekId?: string | null
    testID?: string
}

const TEXTS = {
    since: "Depuis ton dernier passage",
    more: "Lire la suite",
    less: "Réduire",
    show: "Montrer à l'élève",
    regenerate: "Autres énoncés",
    retry: "Réessayer",
    seeLarge: "Voir en grand",
    reading: "Les propositions",
}

/** Au-delà, le texte du champion se replie : la colonne est étroite, la séance continue. */
const HELPER_LINES = 4
/** Un texte plus court tient dans ses lignes : pas de « Lire la suite » pour rien. */
const HELPER_FOLD_FROM = 180
/** L'icône d'un réglage dans la bulle : la matière, la difficulté. */
const SETTING_ICON = 16

/** Mémoïsée : la session se redessine à chaque frappe du champ et à chaque signal du live. */
export const TutorSessionEntry = React.memo(function TutorSessionEntry(props: TutorSessionEntryProps) {
    const { entry, helper, onResult, onGeneration, onPeek, peekId, testID } = props
    const [open, setOpen] = useState(false)

    switch (entry.kind) {
        case "since":
            return <Divider label={TEXTS.since} color="role" spacing="1" testID={testID} />
        case "trace":
            return (
                <Row gap="2" align="start" testID={testID}>
                    <Text size="text-xs" color="text-muted" style={styles.time}>
                        {formatTime(entry.at)}
                    </Text>
                    <Text
                        size={entry.quiet === true ? "text-xs" : "text-sm"}
                        color="text-muted"
                        style={entry.quiet === true ? [styles.flex, styles.quiet] : styles.flex}
                    >
                        {entry.text}
                    </Text>
                </Row>
            )
        case "working":
            return (
                <View testID={testID}>
                    <WaitingText size="text-sm" align="start">
                        {entry.text}
                    </WaitingText>
                </View>
            )
        case "helper": {
            // Le texte du champion, sans sa tête ni son nom : ils sont une fois en haut de la colonne (Wajih, 04/11).
            const folds = entry.text.length > HELPER_FOLD_FROM
            return (
                <Stack gap="1" testID={testID}>
                    {entry.title != null && (
                        <Text size="text-sm" weight="bold" color="text-strong">
                            {entry.title}
                        </Text>
                    )}
                    <Text size="text-sm" color="text-primary" numberOfLines={folds && !open ? HELPER_LINES : 0}>
                        {entry.text}
                    </Text>
                    {folds && (
                        <View style={styles.start}>
                            <TextButton
                                label={open ? TEXTS.less : TEXTS.more}
                                size="sm"
                                onPress={() => setOpen(!open)}
                            />
                        </View>
                    )}
                </Stack>
            )
        }
        case "progress":
            return <GenerationProgress entry={entry} testID={testID} />
        case "ready":
            return (
                <GenerationReady
                    entry={entry}
                    onReview={onGeneration != null ? () => onGeneration(entry.id, "review") : undefined}
                    onSendFirst={onGeneration != null ? () => onGeneration(entry.id, "sendFirst") : undefined}
                    onPeek={onPeek}
                    peekId={peekId}
                    testID={testID}
                />
            )
        case "proposal":
            return (
                <HelperProposal
                    entry={entry}
                    helper={helper}
                    onSee={onGeneration != null ? () => onGeneration(entry.id, "see") : undefined}
                    onSend={onGeneration != null ? () => onGeneration(entry.id, "send") : undefined}
                    onDismiss={onGeneration != null ? () => onGeneration(entry.id, "dismiss") : undefined}
                    testID={testID}
                />
            )
        case "activity":
            return <ActivityGroup entry={entry} testID={testID} />
        case "tutor": {
            const settings = entry.settings ?? []
            return (
                <Stack gap="1" align="end" testID={testID}>
                    <View style={styles.bubble}>
                        {settings.length > 0 ? (
                            // Round 4 : « l'information principale, c'est presque la configuration » — elle
                            // d'abord, lisible ; le texte éventuel dessous.
                            <MessageBubble face="own" tail>
                                <Stack gap="1">
                                    <Row gap="2" style={styles.wrap}>
                                        {settings.map((setting) => (
                                            <Row key={setting.field} gap="1" align="center">
                                                {setting.iconUri != null && (
                                                    <ImageIcon uri={setting.iconUri} size={SETTING_ICON} plate />
                                                )}
                                                <Text size="text-sm" weight="bold" color="text-on-role">
                                                    {setting.label}
                                                </Text>
                                            </Row>
                                        ))}
                                    </Row>
                                    {entry.text !== "" && (
                                        <Text size="text-xs" color="text-on-role">
                                            {entry.text}
                                        </Text>
                                    )}
                                </Stack>
                            </MessageBubble>
                        ) : (
                            <MessageBubble face="own" text={entry.text} tail />
                        )}
                    </View>
                    <Text size="text-xs" color="text-muted">
                        {formatTime(entry.at)}
                    </Text>
                </Stack>
            )
        }
        case "result":
            return <TutorSessionResult entry={entry} onResult={onResult} testID={testID} />
    }
})

/**
 * Une génération sans proposition : la phrase, et « Réessayer ». Des propositions sans relecture (une ligne ancienne) :
 * en version courte, « Voir en grand » les relit côte à côte.
 */
function TutorSessionResult(props: {
    entry: Extract<UITutorSessionEntry, { kind: "result" }>
    onResult?: TutorSessionEntryProps["onResult"]
    testID?: string
}) {
    const { entry, onResult, testID } = props
    // État d'interface : la boîte de lecture « Voir en grand » est ouverte.
    const [reading, setReading] = useState(false)
    const activities = entry.activities
    const regenerate = onResult != null ? () => onResult(entry.id, "regenerate") : undefined

    if (entry.error != null) {
        return (
            <Stack gap="2" testID={testID}>
                <Banner color="danger" tinted icon="exclamation-circle" message={entry.error} />
                {onResult != null && (
                    <View style={styles.start}>
                        <Button
                            label={TEXTS.retry}
                            iconName="redo"
                            variant="outline"
                            size="sm"
                            onPress={() => onResult(entry.id, "retry")}
                        />
                    </View>
                )}
            </Stack>
        )
    }
    const create = (id: string | number) => onResult?.(entry.id, "create", id)
    // « Montrer à l'élève » depuis la boîte de lecture la referme : le masteur voit au centre ce qu'il vient de
    // montrer. Hors de la boîte, elle est déjà fermée.
    const show = (id: string | number) => {
        onResult?.(entry.id, "show", id)
        setReading(false)
    }
    const quiz = (id: string | number) => onResult?.(entry.id, "quiz", id)
    const quizAvailable = entry.quizAvailable === true && onResult != null
    const showAction = onResult != null ? { label: TEXTS.show, iconName: "eye" as const } : undefined
    const anyReady = activities.some((activity) => activity.status === "ready")
    // Les mêmes propositions, entières (la colonne agrandie, la boîte « Voir en grand ») ou courtes (la colonne).
    const results = (compact: boolean) => (
        <GeneratedActivityResults
            activities={activities}
            voice="student"
            compact={compact}
            onCreate={create}
            quizAvailable={quizAvailable}
            onToggleQuiz={quiz}
            extraAction={showAction != null ? { ...showAction, onPress: show } : undefined}
        />
    )
    return (
        <Stack gap="2" testID={testID}>
            {
                // Weekly du 03/11 (W10) : chaque proposition en version courte, qu'on donne à l'élève sans tout
                // lire ; « Voir en grand » les relit côte à côte, dans une boîte de lecture.
                <>
                    {results(true)}
                    {anyReady && (
                        <View style={styles.start}>
                            <Button
                                label={TEXTS.seeLarge}
                                iconName="expand-alt"
                                variant="outline"
                                size="sm"
                                onPress={() => setReading(true)}
                            />
                        </View>
                    )}
                    <Modal visible={reading} onClose={() => setReading(false)} title={TEXTS.reading} wide>
                        {results(false)}
                    </Modal>
                </>
            }
            {onResult != null && anyReady && (
                <Row gap="2" align="center" justify="center" style={styles.wrap}>
                    <Button label={TEXTS.regenerate} iconName="redo" variant="ghost" size="sm" onPress={regenerate} />
                </Row>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    flex: { flex: 1, minWidth: 0 },
    /** Une suggestion rangée sans décision : plus discrète qu'une trace. */
    quiet: { fontStyle: "italic" as const },
    /** L'heure d'une trace : une colonne étroite, que les traces partagent. */
    time: { width: 36 },
    start: { alignItems: "flex-start" as const },
    bubble: { maxWidth: "88%" },
    wrap: { flexWrap: "wrap" as const },
}))
